import React, { createContext, useContext, useEffect, useLayoutEffect, useState, useRef } from "react";
import { licenseService } from "../Services/license.service";
import { useTheme as useBaseTheme } from "./ThemeContext";
import Cookies from "js-cookie";
import themesLicensed from "../Constants/themesLicensed";

const ThemeContext = createContext();

const FIXED_DOMAIN = process.env.REACT_APP_FIXED_DOMAIN || "";

const DEFAULT_LOGO = `${process.env.PUBLIC_URL}/images/logo_ofv2.jpg`;
const DEFAULT_MAIN_IMAGE = `${process.env.PUBLIC_URL}/images/bg-01w.jpg`;

function currentHostname() {
  if (typeof window === "undefined") return "default";
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") {
    try { return FIXED_DOMAIN ? new URL(FIXED_DOMAIN).hostname : "localhost"; } catch { return "localhost"; }
  }
  return h;
}

const COMPANY_COOKIE_KEY = () => `movyx-companyId-${currentHostname()}`;

const THEME_CACHE_KEY = () => `movyx-theme-snapshot-${currentHostname()}`;

function readThemeCache() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(THEME_CACHE_KEY());
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeThemeCache(snapshot) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(THEME_CACHE_KEY(), JSON.stringify(snapshot));
  } catch {
  }
}

const toKebab = (s = "") =>
  s
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .replace(/([a-z])([0-9]+)/gi, "$1-$2")
    .toLowerCase();

const isValidColor = (val) => {
  if (val === null || val === undefined) return false;
  const s = String(val).toLowerCase().trim();
  return s !== "null" && s !== "" && s !== "undefined";
};

const flatten = (obj, parent = "") => {
  const res = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    const name = parent ? `${parent}-${toKebab(k)}` : toKebab(k);
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(res, flatten(v, name));
    } else {
      if (isValidColor(v)) {
        res[name] = v;
      }
    }
  });
  return res;
};

const isAbsoluteUrl = (url) => typeof url === "string" && /^https?:\/\//.test(url);

const cleanString = (val) => {
  if (val === null || val === undefined || String(val).toLowerCase() === "null" || String(val).trim() === "") {
    return null;
  }
  return val;
};

export const NewThemeProvider = ({ children, companyId: propCompanyId }) => {
  const baseTheme = useBaseTheme();
  const isDark = baseTheme ? baseTheme.isDark : false;

  const defaultLightColorsFlat = flatten(themesLicensed[0]?.colors?.light_theme || {});
  const defaultDarkColorsFlat = {
    ...flatten(themesLicensed[0]?.colors?.light_theme || {}),
    ...flatten(themesLicensed[0]?.colors?.dark_theme || {}),
  };

  const [cache] = useState(() => (typeof window !== "undefined" ? readThemeCache() : null));

  const [themeColors, setThemeColors] = useState(() => {
    const defaultColors = isDark ? defaultDarkColorsFlat : defaultLightColorsFlat;
    if (cache) {
      const cacheColors = isDark ? cache.darkColors : cache.lightColors;
      if (cacheColors && Object.keys(cacheColors).length > 0) {
        return { ...defaultColors, ...cacheColors };
      }
    }
    return defaultColors;
  });
  const [platformName, setPlatformName] = useState(() => cache?.platformName || null);
  const [centralPhone, setCentralPhone] = useState(() => cache?.centralPhone || null);
  const [companyPhone, setCompanyPhone] = useState(() => cache?.companyPhone || null);
  const [phrase, setPhrase] = useState(() => cache?.phrase || null);
  const [androidLink, setAndroidLink] = useState(() => cache?.androidLink || null);
  const [iosLink, setIosLink] = useState(() => cache?.iosLink || null);
  const [logoLoading, setLogoLoading] = useState(() => !cache?.companyLogo);
  const [companyLogo, setCompanyLogo] = useState(() => cache?.companyLogo || null);
  const [companyMainImageUrl, setCompanyMainImageUrl] = useState(() => cache?.companyMainImageUrl || null);
  const [favicon, setFavicon] = useState(() => cache?.favicon || null);
  const [condensedMenu, setCondensedMenu] = useState(() => cache?.condensedMenu || DEFAULT_LOGO);

  const lightColorsRef = useRef(
    cache?.lightColors && Object.keys(cache.lightColors).length > 0
      ? { ...defaultLightColorsFlat, ...cache.lightColors }
      : defaultLightColorsFlat
  );
  const darkColorsRef = useRef(
    cache?.darkColors && Object.keys(cache.darkColors).length > 0
      ? { ...defaultDarkColorsFlat, ...cache.darkColors }
      : defaultDarkColorsFlat
  );

  useLayoutEffect(() => {
    const colors = isDark ? darkColorsRef.current : lightColorsRef.current;
    if (Object.keys(colors).length > 0) {
      applyColorsToCSS(colors);
      setThemeColors(colors);
    }
  }, [isDark]);

  useEffect(() => {
    async function fetchAndApplyTheme() {
      let logoResolved = !!cache?.companyLogo;
      let mainImageResolved = !!cache?.companyMainImageUrl;

      try {
        let companyId = propCompanyId;

        if (!companyId) {
          const savedCompanyId = Cookies.get(COMPANY_COOKIE_KEY());
          if (savedCompanyId) companyId = savedCompanyId;
        }

        if (!companyId) {
          const isLocalhost =
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";
          const domain = isLocalhost ? FIXED_DOMAIN : `${window.location.origin}/`;
          if (domain) {
            const licensesByDomain = await licenseService.fetchLicensesByDomain(domain);
            const licenseByDomain =
              Array.isArray(licensesByDomain) && licensesByDomain.length > 0
                ? licensesByDomain[0]
                : null;
            if (licenseByDomain && licenseByDomain.licensedId) {
              companyId = licenseByDomain.licensedId;
              Cookies.set(COMPANY_COOKIE_KEY(), companyId, { expires: 30 });
            }
          }
        }

        if (!companyId) return;

        const licenses = await licenseService.fetchLicenses(companyId);
        const license =
          Array.isArray(licenses) && licenses.length > 0 ? licenses[0] : null;

        if (!license) return;

        let colorsObj = license.colors;
        if (typeof colorsObj === "string") {
          colorsObj = colorsObj.replace(/\\/g, "").trim();
          const firstBrace = colorsObj.indexOf("{");
          const lastBrace = colorsObj.lastIndexOf("}");
          if (firstBrace !== -1 && lastBrace !== -1) {
            colorsObj = colorsObj.substring(firstBrace, lastBrace + 1);
          }
          try {
            colorsObj = JSON.parse(colorsObj);
          } catch (e) {
            console.error("Erro ao converter colors para objeto:", e);
            colorsObj = null;
          }
        }

        let selected = null;
        if (colorsObj && (colorsObj.light_theme || colorsObj.dark_theme)) {
          if (colorsObj.light_theme) {
            lightColorsRef.current = {
              ...defaultLightColorsFlat,
              ...flatten(colorsObj.light_theme),
            };
          }
          if (colorsObj.dark_theme) {
            darkColorsRef.current = {
              ...defaultDarkColorsFlat,
              ...flatten(colorsObj.dark_theme),
            };
          }

          selected = isDark ? darkColorsRef.current : lightColorsRef.current;
          if (selected && Object.keys(selected).length > 0) {
            setThemeColors(selected);
            applyColorsToCSS(selected);
          }
        } else {
          console.error("colorsObj não possui light_theme ou dark_theme");
        }

        const rawLogo = license.companyLogo || null;
        const resolvedLogo = isAbsoluteUrl(rawLogo) ? rawLogo : DEFAULT_LOGO;
        setCompanyLogo(resolvedLogo);
        logoResolved = true;

        const rawCondensedMenu = license.condensedMenu || null;
        const resolvedCondensedMenu = isAbsoluteUrl(rawCondensedMenu)
          ? rawCondensedMenu
          : DEFAULT_LOGO;
        setCondensedMenu(resolvedCondensedMenu);

        const rawMainImage = license.homeScreenImage || null;
        const resolvedMainImage = isAbsoluteUrl(rawMainImage) ? rawMainImage : DEFAULT_MAIN_IMAGE;
        setCompanyMainImageUrl(resolvedMainImage);
        mainImageResolved = true;

        const resolvedPlatformName = cleanString(license.platformName) || "Movyx";
        const resolvedCentralPhone = cleanString(license.centralPhone);
        const resolvedCompanyPhone = cleanString(license.companyPhone);
        const resolvedPhrase = cleanString(license.phrase);
        const resolvedAndroidLink = cleanString(license.androidLink);
        const resolvedIosLink = cleanString(license.iosLink);
        const resolvedFavicon = cleanString(license.favicon);

        setPlatformName(resolvedPlatformName);
        setCentralPhone(resolvedCentralPhone);
        setCompanyPhone(resolvedCompanyPhone);
        setPhrase(resolvedPhrase);
        setAndroidLink(resolvedAndroidLink);
        setIosLink(resolvedIosLink);
        setFavicon(resolvedFavicon);

        writeThemeCache({
          companyLogo: resolvedLogo,
          condensedMenu: resolvedCondensedMenu,
          companyMainImageUrl: resolvedMainImage,
          platformName: resolvedPlatformName,
          centralPhone: resolvedCentralPhone,
          companyPhone: resolvedCompanyPhone,
          phrase: resolvedPhrase,
          androidLink: resolvedAndroidLink,
          iosLink: resolvedIosLink,
          favicon: resolvedFavicon,
          lightColors: lightColorsRef.current,
          darkColors: darkColorsRef.current,
        });
      } finally {
        if (!logoResolved) {
          setCompanyLogo(DEFAULT_LOGO);
        }
        if (!mainImageResolved) {
          setCompanyMainImageUrl(DEFAULT_MAIN_IMAGE);
        }
        setLogoLoading(false);
      }
    }

    fetchAndApplyTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCompanyId]);

  const applyColorsToCSS = (colors) => {
    try {
      const root = document.documentElement;
      Object.keys(colors).forEach((key) => {
        const value = colors[key];
        if (value !== undefined && value !== null) {
          root.style.setProperty(`--${key}`, value);
        }
      });
    } catch (err) {
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        themeColors,
        logoLoading,
        companyLogo,
        companyMainImageUrl,
        platformName,
        centralPhone,
        companyPhone,
        phrase,
        androidLink,
        iosLink,
        favicon,
        condensedMenu,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
